import { useState } from 'react';
import { useDashboardConfig } from '../context/DashboardContext';
import EngagementMap from './EngagementMap';

const CONNECTION_TYPES = [
  'Resident',
  'Elected Official',
  'Funder',
  'Researcher'
];

export default function EngagementForm({ indicators, onSubmit, onReset, submitting, submitted }) {
  const config = useDashboardConfig();
  const { state, county } = config.org.geography;

  const [name, setName] = useState('');
  const [connectionType, setConnectionType] = useState('');
  const [indicatorId, setIndicatorId] = useState('');
  const [locationExpanded, setLocationExpanded] = useState(false);
  const [pickerLocation, setPickerLocation] = useState(null);
  const [question, setQuestion] = useState('');
  const [email, setEmail] = useState('');
  const [improvement, setImprovement] = useState('');
  const [validationError, setValidationError] = useState('');

  function handleLocationDropdown(val) {
    const expanded = val === 'yes';
    setLocationExpanded(expanded);
    if (!expanded) setPickerLocation(null);
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!question.trim()) {
      setValidationError('A question or comment is required.');
      return;
    }
    setValidationError('');
    onSubmit({
      name: name.trim() || null,
      connection_type: connectionType || null,
      indicator_id: indicatorId || null,
      has_location: pickerLocation !== null,
      location_lng: pickerLocation?.[0] ?? null,
      location_lat: pickerLocation?.[1] ?? null,
      question: question.trim(),
      email: email.trim() || null,
      improvement: improvement.trim() || null
    });
  }

  if (submitted) {
    return (
      <div className="engagement-form engagement-success">
        <p>Thanks for sharing! Your submission has been received.</p>
        <button
          className="engagement-submit-btn"
          onClick={() => {
            setName('');
            setConnectionType('');
            setIndicatorId('');
            setLocationExpanded(false);
            setPickerLocation(null);
            setQuestion('');
            setEmail('');
            setImprovement('');
            onReset?.();
          }}
        >
          Submit another
        </button>
      </div>
    );
  }

  return (
    <form className="engagement-form" onSubmit={handleSubmit} noValidate>
      <div className="engagement-field">
        <label className="engagement-label" htmlFor="eng-name">
          Name <span className="engagement-optional">(optional)</span>
        </label>
        <input
          id="eng-name"
          type="text"
          className="engagement-input"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Your name"
        />
      </div>

      <div className="engagement-field">
        <label className="engagement-label" htmlFor="eng-connection">
          Your connection to this community
        </label>
        <select
          id="eng-connection"
          className="engagement-select"
          value={connectionType}
          onChange={e => setConnectionType(e.target.value)}
        >
          <option value="">Select one (optional)</option>
          {CONNECTION_TYPES.map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      <div className="engagement-field">
        <label className="engagement-label" htmlFor="eng-outcome">
          Related outcome
        </label>
        <select
          id="eng-outcome"
          className="engagement-select"
          value={indicatorId}
          onChange={e => setIndicatorId(e.target.value)}
        >
          <option value="">Not specific to one outcome</option>
          {(indicators ?? []).map(ind => (
            <option key={ind.id} value={ind.id}>{ind.label}</option>
          ))}
        </select>
      </div>

      <div className="engagement-field">
        <label className="engagement-label" htmlFor="eng-location">
          Does your comment relate to a specific location?
        </label>
        <select
          id="eng-location"
          className="engagement-select"
          value={locationExpanded ? 'yes' : 'no'}
          onChange={e => handleLocationDropdown(e.target.value)}
        >
          <option value="no">No specific location</option>
          <option value="yes">Yes — show me the map</option>
        </select>
        {locationExpanded && (
          <div className="engagement-picker-map">
            <p className="engagement-picker-hint">
              {pickerLocation
                ? `Pin placed at ${pickerLocation[1].toFixed(4)}, ${pickerLocation[0].toFixed(4)}. Drag to adjust.`
                : 'Click on the map to drop a pin.'}
            </p>
            <EngagementMap
              state={state}
              county={county}
              geography={[]}
              indicatorConfig={null}
              gradient={null}
              submissions={[]}
              pickerMode
              pickerLocation={pickerLocation}
              onPickerChange={setPickerLocation}
            />
          </div>
        )}
      </div>

      <div className="engagement-field">
        <label className="engagement-label" htmlFor="eng-question">
          Question or comment <span style={{ color: '#c8614a' }}>*</span>
        </label>
        <textarea
          id="eng-question"
          className="engagement-textarea"
          rows={4}
          value={question}
          onChange={e => setQuestion(e.target.value)}
          placeholder="What's on your mind about this community?"
          required
        />
      </div>

      <div className="engagement-field">
        <label className="engagement-label" htmlFor="eng-email">
          Email address <span className="engagement-optional">(optional — so we can get back to you)</span>
        </label>
        <input
          id="eng-email"
          type="email"
          className="engagement-input"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="you@example.com"
        />
      </div>

      <div className="engagement-field">
        <label className="engagement-label" htmlFor="eng-improvement">
          Dashboard improvement ideas <span className="engagement-optional">(optional)</span>
        </label>
        <textarea
          id="eng-improvement"
          className="engagement-textarea"
          rows={3}
          value={improvement}
          onChange={e => setImprovement(e.target.value)}
          placeholder="Any thoughts on improving this dashboard?"
        />
      </div>

      {validationError && (
        <p className="engagement-validation-error">{validationError}</p>
      )}

      <button
        type="submit"
        className="engagement-submit-btn"
        disabled={submitting}
      >
        {submitting ? 'Submitting…' : 'Submit'}
      </button>
    </form>
  );
}
